import { FUEL_TYPE_MAP, STATE_MAPPER } from "../docs/constants";
import { compareFeeLabels } from "./feeItems";
import { formatDate } from "./formatDate";

export const generateDocsReport = (docsFormData, docsOutput) => {
  const excludedKeys = ['Inputs', 'TransactionID', 'Type', 'Notes'];

  // Sorted by document name so the Documents Output order is deterministic and
  // matches the bot's summary PDF, which now sorts the same way. Previously both
  // sides just followed whatever order ATC returned <Documents> in.
  //
  // compareFeeLabels is the Fee Output comparator, reused here so documents and
  // fee line items order identically. It trims and lowercases (so a stray
  // leading space no longer sorts a document above every other one) and compares
  // digit runs numerically (so "Attachment 2" stays ahead of "Attachment 10").
  // The previous `sensitivity: 'base'` comparator did neither. Its Python mirror
  // is label_sort_key in DMV-bot-refi-docs/generate_docs_html.py.
  const documents = Object.entries(docsOutput || {})
    .filter(([key, value]) => !excludedKeys.includes(key) && Array.isArray(value))
    .sort(([a], [b]) => compareFeeLabels(a, b));

  const nonLinkItems = documents.filter(([, [pdfUrl]]) => !pdfUrl);
  const linkItems = documents.filter(([, [pdfUrl]]) => pdfUrl);
  const notes = docsOutput?.Notes;

  // This function returns an HTML *string* for html2pdf, not JSX. The previous
  // version interpolated React attributes into that string — `key=0
  // dangerouslySetInnerHTML={{ __html: ...`  — which the parser read as bogus
  // attributes and then printed `__html: <note text>}}` as visible text in the
  // PDF. Emit the note as plain markup instead, and drop empty fragments so
  // "Notes" never shows a bullet with nothing beside it.
  const notesHtml = notes
    ?.split('|')
    .map((note) => (note ? note.replaceAll('<a ', '<a target="_blank" ') : ''))
    .filter((note) => note.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim().length)
    .map((note) => `<li class="sanitizedHtml">${note.trim()}</li>`)

  const nonLinkItemsHtml = nonLinkItems.map(([key, [, html]]) => {
    // Clean the incoming HTML (handle links so they open in a new tab when clicked)
    let cleanHtml = html ? html.replaceAll('<a ', '<a target="_blank" ') : '';

    // We split the content by by common separators and block tags to isolate each potential list item,
    // then wrap them in a Flexbox div that forces the bullet and text to align.
    // This is so html2canvas renders the bullets propperly
    const bulletizedContent = cleanHtml
      .split(/<li>|<\/li>|<br\s*\/?>|<\/?p>|<\/?div>/g) // Split by common separators and block tags
      .filter(item => {
        // Remove all HTML tags and &nbsp; to see if there's actual text
        const textOnly = item.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim(); // Removes stray tags and invisible spaces
        return textOnly.length > 0; // If nothing is left move on to the next item as returning would cause a blank bullet point
      })
      .map(text => `
        <div class="pdf-avoid-break" style="display: flex; align-items: flex-start; margin-bottom: 4px; page-break-inside: avoid; break-inside: avoid;">
          <span style="margin-right: 8px; flex-shrink: 0; font-weight: bold;">•</span>
          <div style="flex: 1;">${text.trim()}</div>
        </div>
      `).join('');

    // pdf-avoid-break: html2pdf's pagebreak `avoid` selector picks this up and
    // shifts the whole card to the next page rather than slicing it in half.
    return `
      <div key="${key}" class="bg-white p-6 rounded-xl border text-sm docs-output-form pdf-avoid-break" style="page-break-inside: avoid; break-inside: avoid;">
        <h4 class="font-medium text-gray-800 mb-2">${key}</h4>
        <div class="docs-list-output text-xs text-gray-700 sanitizedHtml">
          ${bulletizedContent || cleanHtml}
        </div>
      </div>`;
  });

  const linkItemsHtml = linkItems.map(([key, [pdfUrl, html]]) => {
    // Clean the HTML and handle links
    const cleanHtml = html ? html.replaceAll('<a ', '<a target="_blank" style="color: #2563eb; text-decoration: underline;" ') : '';

    // Map the content into flex-rows to prevent the "floating bullet" bug
    const bulletizedContent = cleanHtml
      .split(/<li>|<\/li>|<br\s*\/?>|<\/?p>|<\/?div>/g)    // Split by common separators and block tags
      .filter(item => {                                   // Ensure there is actual text content
        const textOnly = item.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
        return textOnly.length > 0;
      })
      .map(text => `
        <div class="pdf-avoid-break" style="display: flex; align-items: flex-start; margin-top: 4px; width: 100%; page-break-inside: avoid; break-inside: avoid;">
          <span style="margin-right: 8px; flex-shrink: 0; font-weight: bold; color: #4b5563;">•</span>
          <div style="flex: 1; word-break: break-word;">${text.trim()}</div>
        </div>
      `).join('');

    // pdf-avoid-break keeps a single document row (title + its notes + the
    // download icon) on one page. Without it a long entry such as "Original
    // Power of Attorney" could be sliced across the page boundary.
    return `
      <div key="${key}" class="flex justify-between items-start border-b pb-4 last:border-0 pdf-avoid-break" style="page-break-inside: avoid; break-inside: avoid;">
        <div style="flex: 1;">
          <h4 class="font-medium text-gray-800">${key}</h4>
          ${html ? `
            <div class="docs-list-output mt-1 text-xs text-gray-600 sanitizedHtml">
              ${bulletizedContent}
            </div>
          ` : ``}
        </div>
        <a
          href="${encodeURI(pdfUrl)}"
          download
          target="_blank"
          rel="noopener noreferrer"
          class="border border-gray-300 w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer ml-4 shrink-0"
        >
          <img src="/download-icon.png" alt="" style="width: 16px; height: 16px;">
        </a>
      </div>`;
  });



  const getStateFullName = (abbr) => {
    return STATE_MAPPER[abbr] || abbr;
  };

  const convertArray = (arr) => {
    let str = "";
    for (let i = 0; i < arr?.length; i++) {
      str += arr[i];
    }

    return str;
  }

  return (
    `<style>
      /*
        Ported from the bot's print CSS (DMV-bot-refi-docs/generate_docs_html.py),
        which paginates this same report more cleanly. Protecting the label/value
        rows inside an already-protected block is what stops a row being sliced
        between its label and its value.

        This is the declarative half of the fix: html2pdf's 'css' pagebreak mode
        reads these properties off the computed style, and the 'avoid' selectors
        in pages/docsOutput/index.js and pages/multiPartFormOutput/index.js
        target the same elements, so both of html2pdf's code paths agree on what
        must not be split.

        Kept scoped to .pdf-avoid-break descendants. A bare '.flex-row' would
        match the section wrappers too and degrade into the 'avoid-all'
        behaviour that produced large blank runs.
      */
      .pdf-avoid-break,
      .pdf-avoid-break .flex-row {
        page-break-inside: avoid;
        break-inside: avoid;
      }
    </style>
    <div class="default-bg-color p-6">
      <div class='flex flex-col gap-4 w-full'>
        <div class='flex flex-row text-gray-700 font-bold mb-2 items-center h-11'>
          Your Information
        </div>

        <div class='bg-white p-6 rounded-xl text-sm border border-gray-300 pdf-avoid-break' style="page-break-inside: avoid; break-inside: avoid;">
          <div class='text-gray-700 mb-3 font-medium'>
            Location Information
          </div>

          <div class='flex flex-col lg:flex-row w-full gap-6 lg:gap-24 mb-6 pdf-avoid-break' style="page-break-inside: avoid; break-inside: avoid;">
            <div class='flex flex-col w-full text-gray-700 gap-2'>
              ${docsFormData?.zipCode ? `
                <div class='flex flex-row justify-between'>
                  <div>Zip Code:</div>
                  <div class='font-medium'>${docsFormData.zipCode}</div>
                </div>
              ` : ""}

              ${docsFormData?.city ? `
                <div class='flex flex-row justify-between'>
                  <div>City:</div>
                  <div class='font-medium'>${docsFormData.city}</div>
                </div>
              ` : ""}

            </div>

            <div class='flex flex-col w-full text-gray-700 gap-2'>
              ${docsFormData?.state ? `
                <div class='flex flex-row justify-between'>
                  <div>
                    State:
                  </div>
                  <div class='font-medium'>
                    ${getStateFullName(docsFormData?.state)}
                  </div>
                </div>
              ` : ""
              }

              ${docsFormData?.country ? `
                <div class='flex flex-row justify-between'>
                  <div>
                    County:
                  </div>
                  <div class='font-medium'>
                    ${docsFormData?.country}
                  </div>
                </div>
              ` : ""
              }
            </div>
          </div>

          <div>
            <div class='border-t-2 border-t-gray-100 mb-3'>
              <div class='text-gray-700 mt-6 font-bold'>
                Vehicle Information <span class='text-xs text-gray-600 font-normal ml-1'>${docsFormData?.vinCode ? docsFormData?.vinCode : ''}</span>
              </div>
            </div>

            <div class='flex flex-col lg:flex-row w-full gap-6 lg:gap-24 mb-6 pdf-avoid-break' style="page-break-inside: avoid; break-inside: avoid;">
              <div class='flex flex-col w-full text-gray-700 gap-2'>
                ${docsFormData?.vehicleType ? `
                  <div class='flex flex-row justify-between'>
                    <div>Vehicle Type:</div>
                    <div class='font-medium'>${docsFormData.vehicleType}</div>
                  </div>
                ` : ""}

                ${docsFormData?.modelYear ? `
                  <div class='flex flex-row justify-between'>
                    <div>Model Year:</div>
                    <div class='font-medium'>${docsFormData.modelYear}</div>
                  </div>
                ` : ""}

                ${docsFormData?.model ? `
                  <div class='flex flex-row justify-between'>
                    <div>Model:</div>
                    <div class='font-medium'>${docsFormData.model}</div>
                  </div>
                ` : ""}

                ${docsFormData?.numberOfAxles ? `
                  <div class='flex flex-row justify-between'>
                    <div>Number of Axles:</div>
                    <div class='font-medium'>${docsFormData.numberOfAxles}</div>
                  </div>
                ` : ""}

                ${docsFormData?.fuelType ? `
                  <div class='flex flex-row justify-between'>
                    <div>Fuel Type:</div>
                    <div class='font-medium'>${FUEL_TYPE_MAP[docsFormData.fuelType]}</div>
                  </div>
                ` : ""}

                ${docsFormData?.registrationMonths ? `
                  <div class='flex flex-row justify-between'>
                    <div>Registration Months:</div>
                    <div class='font-medium'>${docsFormData.registrationMonths}</div>
                  </div>
                ` : ""}

                ${docsFormData?.odometer ? `
                  <div class='flex flex-row justify-between'>
                    <div>Odometer (miles): </div>
                    <div class='font-medium'>${Number(docsFormData?.odometer).toLocaleString()}</div>
                  </div>
                ` : ""}

                ${docsFormData?.color ? `
                  <div class='flex flex-row justify-between'>
                    <div>Color:</div>
                    <div class='font-medium'>${docsFormData.color}</div>
                  </div>
                ` : ""}

                ${docsFormData?.gvw ? `
                  <div class='flex flex-row justify-between'>
                    <div>GVW:</div>
                    <div class='font-medium'>${Number(docsFormData?.gvw).toLocaleString()} lbs</div>
                  </div>
                ` : ""}

                ${docsFormData?.horsepower ? `
                  <div class='flex flex-row justify-between'>
                    <div>Horsepower:</div>
                    <div class='font-medium'>${Number(docsFormData?.horsepower).toLocaleString()}</div>
                  </div>
                ` : ""}

                
              </div>

              <div class='flex flex-col w-full text-gray-700 gap-2'>
                ${docsFormData?.condition ? `
                  <div class='flex flex-row justify-between'>
                    <div>Condition:</div>
                    <div class='font-medium'>${docsFormData.condition}</div>
                  </div>
                ` : ""}

                ${docsFormData?.make ? `
                  <div class='flex flex-row justify-between'>
                    <div>Make:</div>
                    <div class='font-medium'>${docsFormData.make}</div>
                  </div>
                ` : ""}

                ${docsFormData?.bodyStyle ? `
                  <div class='flex flex-row justify-between'>
                    <div>Body Style:</div>
                    <div class='font-medium'>${docsFormData.bodyStyle}</div>
                  </div>
                ` : ""}

                ${docsFormData?.numberOfTires ? `
                  <div class='flex flex-row justify-between'>
                    <div>Number of Tires:</div>
                    <div class='font-medium'>${docsFormData.numberOfTires}</div>
                  </div>
                ` : ""}

                ${docsFormData?.cylinders ? `
                  <div class='flex flex-row justify-between'>
                    <div>Cylinders:</div>
                    <div class='font-medium'>${docsFormData.cylinders}</div>
                  </div>
                ` : ""}

                ${docsFormData?.rimSize ? `
                  <div class='flex flex-row justify-between'>
                    <div>Rim Size (Inches):</div>
                    <div class='font-medium'>${docsFormData.rimSize}</div>
                  </div>
                ` : ""}

                ${docsFormData?.vehicleWeight ? `
                  <div class='flex flex-row justify-between'>
                    <div>Vehicle Weight:</div>
                    <div class='font-medium'>${Number(docsFormData?.vehicleWeight).toLocaleString()} lbs</div>
                  </div>
                ` : ""}

                ${docsFormData?.mpg ? `
                  <div class='flex flex-row justify-between'>
                    <div>MPG (Average):</div>
                    <div class='font-medium'>${docsFormData.mpg}</div>
                  </div>
                ` : ""}

                ${docsFormData?.msrp ? `
                  <div class='flex flex-row justify-between'>
                    <div>MSRP:</div>
                    <div class='font-medium'>
                      ${new Intl.NumberFormat('en-US', { 
                      style: 'currency', 
                      currency: 'USD' 
                    }).format(docsFormData.msrp)}
                    </div>
                  </div>
                ` : ""}
              </div>
            </div>
          </div>

          <div>
            <div class='border-t-2 border-t-gray-100 mb-3'>
              <div class='text-gray-700 mt-6 font-bold'>
                Transaction Information
              </div>
            </div>

            <div class='flex flex-col lg:flex-row w-full gap-6 lg:gap-24 mb-6 pdf-avoid-break' style="page-break-inside: avoid; break-inside: avoid;">
              <div class='flex flex-col w-full text-gray-700 gap-2'>
                ${docsFormData?.transactionType ? `
                  <div class='flex flex-row justify-between'>
                    <div>
                      Transaction Type:
                    </div>
                    <div class='font-medium'>
                      ${docsFormData.transactionType}
                    </div>
                  </div>
                ` : ``}

                ${docsFormData?.uploadDate ? `
                  <div class='flex flex-row justify-between'>
                    <div>Uploaded Date:</div>
                    <div class='font-medium'>${formatDate(docsFormData.uploadDate)}</div>
                  </div>
                ` : ""}

                ${docsFormData?.plateNumber ? `
                  <div class='flex flex-row justify-between'>
                    <div>Plate Number:</div>
                    <div class='font-medium'>${docsFormData.plateNumber}</div>
                  </div>
                ` : ""}

                ${docsFormData?.docsFees ? `
                  <div class='flex flex-row justify-between'>
                    <div>Doc Fees:</div>
                    <div class='font-medium'>
                      ${new Intl.NumberFormat('en-US', { 
                      style: 'currency', 
                      currency: 'USD' 
                    }).format(docsFormData.docsFees)}
                    </div>
                  </div>
                ` : ""}

                ${docsFormData?.extendedWarranties ? `
                  <div class='flex flex-row justify-between'>
                    <div>Extended Warranties:</div>
                    <div class='font-medium'>
                      ${new Intl.NumberFormat('en-US', { 
                      style: 'currency', 
                      currency: 'USD' 
                    }).format(docsFormData.extendedWarranties)}
                    </div>
                  </div>
                ` : ""}

                ${docsFormData?.protectionPackages ? `
                  <div class='flex flex-row justify-between'>
                    <div>Protection Packages:</div>
                    <div class='font-medium'>
                      ${new Intl.NumberFormat('en-US', { 
                      style: 'currency', 
                      currency: 'USD' 
                    }).format(docsFormData.protectionPackages)}
                    </div>
                  </div>
                ` : ""}

                <div class='flex flex-row justify-between'>
                  <div>Military:</div>
                  <div class='font-medium'>${docsFormData.military ? "Yes" : "No"}</div>
                </div>

                <div class='flex flex-row justify-between'>
                  <div>Lien:</div>
                  <div class='font-medium'>${docsFormData.lien ? "Yes" : "No"}</div>
                </div>
              </div>

              <div class='flex flex-col w-full text-gray-700 gap-2'>
                ${docsFormData?.transactionDate ? `
                  <div class='flex flex-row justify-between'>
                    <div>Transaction Date:</div>
                    <div class='font-medium'>${formatDate(docsFormData.transactionDate)}</div>
                  </div>
                ` : ""}

                ${docsFormData?.plateType ? `
                  <div class='flex flex-row justify-between'>
                    <div>Plate Type:</div>
                    <div class='font-medium'>${docsFormData.plateType}</div>
                  </div>
                ` : ""}

                ${docsFormData?.basePrice ? `
                  <div class='flex flex-row justify-between'>
                    <div>Base Price:</div>
                    <div class='font-medium'>
                      ${new Intl.NumberFormat('en-US', { 
                      style: 'currency', 
                      currency: 'USD' 
                    }).format(docsFormData.basePrice)}
                    </div>
                  </div>
                ` : ""}

                ${docsFormData?.totalPrice ? `
                  <div class='flex flex-row justify-between'>
                    <div>Total Price:</div>
                    <div class='font-medium'>
                      ${new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    }).format(docsFormData.totalPrice)}
                    </div>
                  </div>
                ` : ""}

                ${docsFormData?.additionalFees ? `
                  <div class='flex flex-row justify-between'>
                    <div>Additional Fee:</div>
                    <div class='font-medium'>
                      ${new Intl.NumberFormat('en-US', { 
                      style: 'currency', 
                      currency: 'USD' 
                    }).format(docsFormData.additionalFees)}
                    </div>
                  </div>
                ` : ""}

                ${docsFormData?.insurancePackages ? `
                  <div class='flex flex-row justify-between'>
                    <div>Insurance Packages:</div>
                    <div class='font-medium'>
                      ${new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    }).format(docsFormData.insurancePackages)}
                    </div>
                  </div>
                ` : ""}

                ${docsFormData?.theftProtection ? `
                  <div class='flex flex-row justify-between'>
                    <div>Theft Protection:</div>
                    <div class='font-medium'>
                      ${new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    }).format(docsFormData.theftProtection)}
                    </div>
                  </div>
                ` : ""}

                  <div class='flex flex-row justify-between'>
                    <div>
                      Plate Transfer:
                    </div>
                    <div class='font-medium'>
                      ${docsFormData?.plateTransfer ? "Yes" : "No"}
                    </div>
                  </div>

                 ${docsFormData?.driverCategory ? `
                  <div class='flex flex-row justify-between'>
                    <div>Driver Category:</div>
                    <div class='font-medium'>${docsFormData.driverCategory}</div>
                  </div>
                ` : ""}
              </div>
            </div>
          </div>

          ${( docsFormData?.insuranceCompany || docsFormData?.insuranceExpirationDate || docsFormData?.insuranceDate || docsFormData?.insurancePolicyNumber) ? `
            <div>
              <div class='border-t-2 border-t-gray-100 mb-3'>
                <div class='text-gray-700 mt-6 font-medium'>
                  Insurance Information
                </div>
              </div>

              <div class='flex flex-col lg:flex-row w-full gap-6 lg:gap-24 mb-6 pdf-avoid-break' style="page-break-inside: avoid; break-inside: avoid;">
                <div class='flex flex-col w-full text-gray-700 gap-2'>
                  <div class='flex flex-row justify-between'>
                    <div>Insurance Company:</div>
                    <div class='font-medium'>${docsFormData.insuranceCompany}</div>
                  </div>

                  <div class='flex flex-row justify-between'>
                    <div>Insurance Expiration Date:</div>
                    <div class='font-medium'>${formatDate(docsFormData.insuranceExpirationDate)}</div>
                  </div>
                </div>

                <div class='flex flex-col w-full text-gray-700 gap-2'>
                  <div class='flex flex-row justify-between'>
                    <div>Insurance Date:</div>
                    <div class='font-medium'>${formatDate(docsFormData.insuranceDate)}</div>
                  </div>

                  <div class='flex flex-row justify-between'>
                    <div>Insurance Number:</div>
                    <div class='font-medium'>${docsFormData.insurancePolicyNumber}</div>
                  </div>
                </div>
              </div>
            </div>
          ` : "" }

          ${docsOutput?.TransactionID ? `
            <div class='flex flex-col sm:flex-row justify-between break-words gap-1 mt-6' style="page-break-inside: avoid; break-inside: avoid;">
              <div>Transaction ID:</div>
              <div class='font-medium max-w-full break-all text-right sm:text-left'>${docsOutput.TransactionID}</div>
            </div>
          ` : ""}
        </div>
      </div>

      <!--
        Forces Documents Output onto a fresh page. This div previously carried
        BOTH the legacy .html2pdf__page-break class and an inline
        page-break-before, which html2pdf read as two separate rules: it inserted
        one spacer *before* the div (break-before) and a second spacer *after* it
        (legacy class). The second spacer was sized from the div's pre-shift
        position, so it landed at the top of the new page as a large blank band.
        Keeping only the legacy class produces exactly one spacer.
      -->
      <div class="html2pdf__page-break"></div>

      <!--
        Block, not flex: html2pdf pushes an element to the next page by injecting
        a plain <div> spacer as a sibling. Inside a flex column that spacer
        becomes a flex item and can be shrunk or mis-sized, so the layout below
        is a normal block flow with margin-based spacing instead.
      -->
      <div class="mt-4 w-full space-y-6">
        <div class="flex justify-between items-center">
          <div class="flex flex-row text-gray-700 font-bold items-center h-11">Documents Output</div>
        </div>

        ${notes ? `
          <div class="bg-white p-6 rounded-xl border text-sm docs-output-form pdf-avoid-break" style="page-break-inside: avoid; break-inside: avoid;">
            <h4 class="text-gray-800 font-semibold mb-4">Notes</h4>
            <ul>
              ${convertArray(notesHtml)}
            </ul>
          </div>
        ` : ''}

        ${convertArray(nonLinkItemsHtml)}

        <div class="bg-white p-6 rounded-xl border text-sm docs-output-form space-y-6">
          ${convertArray(linkItemsHtml)}
        </div>
      </div>
    </div>
    `
  )
}